import { type Chain, http } from 'viem'
import { WagmiProvider } from 'wagmi'
import { metaMask, safe, walletConnect } from 'wagmi/connectors'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createWeb3Modal } from '@web3modal/wagmi/react'
import { defaultWagmiConfig } from '@web3modal/wagmi/react/config'

import { supportedChains } from '@src/configs/supportedChains'

// 0. Setup queryClient
const queryClient = new QueryClient()

// 1. Get projectId
const projectId = '3c8783f535be44f93360ee0638bd8bdd'

// 2. Create metadata config
const metadata = {
    name: 'Mach',
    description: 'Mach Protocol liquidity layer for Web3.0',
    url: window.location.origin,
    icons: ['https://avatars.githubusercontent.com/u/37784886'],
}

// QuickNode RPC URLs
const QUICKNODE_RPCS = {
    1: 'https://long-capable-tree.quiknode.pro/4f8abee71b92694624d5f6a5aac43bf273b76db1/', // ethereum
    10: 'https://alien-dry-lake.optimism.quiknode.pro/9e3364e544a78fa0581658f542d58d8c02cd13ba/', // optimism
    56: 'https://necessary-thrumming-diamond.bsc.quiknode.pro/f2f3a08170d298c0bd742d77ea0462afcec15dd4/', // bsc
    97: 'https://fittest-shy-orb.bsc-testnet.quiknode.pro/4d8b9a2d005aa4ed2cefcac0a181aef03ec9a8c3/', // bsc testnet
    137: 'https://holy-restless-vineyard.matic.quiknode.pro/559b966d2ea5ab37e95abee0cf1049a10971e30d/', // polygon
    204: 'https://opbnb-rpc.publicnode.com', // opbnb
    8453: 'https://polished-spring-star.base-mainnet.quiknode.pro/19455fd433fb2639609315f8588c3a58a5a9a10f/', // base
    42161: 'https://prettiest-patient-scion.arbitrum-mainnet.quiknode.pro/2d53fa7ffc71e31afb3113f96c54519fcd6516e2/', // arbitrum
    42220: 'https://attentive-alien-water.celo-mainnet.quiknode.pro/9c1b86178a6c7ebc5c772663ac82b51003ef8a81/', // celo
    43114: 'https://withered-soft-field.avalanche-mainnet.quiknode.pro/f0478bf89e96d35ee8351213a1120fe4ba292849/ext/bc/C/rpc/', // avalanche
    81457: 'https://warmhearted-few-scion.blast-mainnet.quiknode.pro/22d44d1f9a1a57adf90c0efd2d644977c3dd5a23/', // blast
    534352: 'https://sleek-crimson-flower.scroll-mainnet.quiknode.pro/37e22c7823176a82f7cea7b89b5c37786b76a810/', // scroll
    5000: 'https://still-compatible-wind.mantle-mainnet.quiknode.pro/277b01e4afb73df889a08ba018dc14e354f5f0da/', // mantle
    34443: 'https://1rpc.io/mode', // mode
    130: 'https://unichain-mainnet.g.alchemy.com/v2/yHZvr09DRycjGM-t-j5hkUvOgfgx8gbt', // unichain
    10143: 'https://monad-testnet.g.alchemy.com/v2/WfV3XMdt1S1vQL5APKi4UZHhSY7VYaEj', // monad testnet
    11155111: 'https://eth-sepolia.g.alchemy.com/v2/WfV3XMdt1S1vQL5APKi4UZHhSY7VYaEj', // sepolia
    6342: 'https://carrot.megaeth.com/rpc', // megaeth testnet
}

export const config = defaultWagmiConfig({
    chains: supportedChains as [Chain, ...Chain[]],
    projectId,
    metadata,
    connectors: [metaMask(), walletConnect({ projectId }), safe()],
    transports: Object.fromEntries(
        supportedChains.map(chain => [
            chain.id,
            http(QUICKNODE_RPCS[chain.id as keyof typeof QUICKNODE_RPCS] || QUICKNODE_RPCS[1]),
        ])
    ),
    enableInjected: false,
})

// Initialize Web3Modal
createWeb3Modal({
    wagmiConfig: config,
    projectId,
    enableAnalytics: true,
    enableOnramp: true,
    themeVariables: {
        '--w3m-accent': '#9e58fb',
        '--w3m-z-index': 10000,
    },
})

export function Web3ModalProvider({ children }: React.PropsWithChildren) {
    return (
        <WagmiProvider config={config}>
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </WagmiProvider>
    )
}
